import { useCallback, useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { FilterField } from '../FilterField'
import {
  FilterState,
  createDefaultFilterState,
} from '@/utilities/types/shared-filter'
import type {
  FilterConfig,
  UpdateDateRangeParams,
} from '@/utilities/types/shared-filter'
import { UserFilterField } from './UserFilterField'

type AlertSeverity = 'success' | 'error' | 'warning' | 'info'

export interface FilterUserModalProps<T = any> {
  open: boolean
  onClose: () => void
  config: FilterConfig<T>
  onApplyFilter: (filters: FilterState<T>) => void
  initialFilters: FilterState<T>
  tab?: string
  optimizedFilterUsers: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  handleAlertMessage: (message: string, severity: AlertSeverity) => void
  handleReset: () => void
  optimizedFilterRoleUsers: (roleName: string) => void
}

export function FilterUserModal<T>({
  open,
  onClose,
  config,
  onApplyFilter,
  initialFilters,
  tab,
  optimizedFilterUsers,
  handleAlertMessage,
  handleReset,
  optimizedFilterRoleUsers
}: FilterUserModalProps<T>) {
  const [currentFilters, setCurrentFilters] = useState(() => ({
    ...createDefaultFilterState<T>(),
    ...initialFilters,
  }))

  const handleUpdateFilter = useCallback((field: string, value: string[]) => {
    setCurrentFilters((prev) => {
      const updatedFilters = {
        ...prev,
        regularFilters: {
          ...prev.regularFilters,
          [field]: value,
        },
      }
      return updatedFilters
    })
  }, [])

  const handleDateRangeChange = useCallback(
    ({ field, range }: UpdateDateRangeParams) => {
      console.log('field and range ', field, range)
      setCurrentFilters((prev) => ({
        ...prev,
        dateRanges: {
          ...prev.dateRanges,
          [field]: range,
        },
      }))
    },
    [],
  )
  const handleApply = useCallback(() => {
    const { createdDate, modifiedDate } = currentFilters.dateRanges
    const { roleName } = currentFilters.regularFilters
  
    // Check if created date start is greater than end
    if (
      createdDate?.start &&
      createdDate?.end &&
      createdDate.start > createdDate.end
    ) {
      handleAlertMessage('Created date start cannot be greater than end date.', 'error')
      return
    }
  
    // Check if modified date start is greater than end
    if (
      modifiedDate?.start &&
      modifiedDate?.end &&
      modifiedDate.start > modifiedDate.end
    ) {
      handleAlertMessage('Modified date start cannot be greater than end date.', 'error')
      return
    }
  
    // Check if modified date is earlier than created date
    if (
      createdDate?.start &&
      modifiedDate?.start &&
      modifiedDate.start < createdDate.start
    ) {
      handleAlertMessage('Modified date cannot be earlier than created date.', 'error')
      return
    }
  
    // Apply filters for created and modified dates if valid
    if (createdDate?.start && createdDate?.end) {
      const startCreatedDate = createdDate.start || ''
      const endCreatedDate = createdDate.end || ''
      const startModifiedDate = modifiedDate?.start || ''
      const endModifiedDate = modifiedDate?.end || ''
  
      optimizedFilterUsers(
        startCreatedDate,
        endCreatedDate,
        startModifiedDate,
        endModifiedDate,
      )
    }
  
    if (modifiedDate?.start && modifiedDate?.end) {
      const startCreatedDate = createdDate?.start || ''
      const endCreatedDate = createdDate?.end || ''
      const startModifiedDate = modifiedDate.start || ''
      const endModifiedDate = modifiedDate.end || ''
  
      optimizedFilterUsers(
        startCreatedDate,
        endCreatedDate,
        startModifiedDate,
        endModifiedDate,
      )
    }
  
    if (
      createdDate?.start &&
      createdDate?.end &&
      modifiedDate?.start &&
      modifiedDate?.end
    ) {
      const startCreatedDate = createdDate.start || ''
      const endCreatedDate = createdDate.end || ''
      const startModifiedDate = modifiedDate.start || ''
      const endModifiedDate = modifiedDate.end || ''
  
      optimizedFilterUsers(
        startCreatedDate,
        endCreatedDate,
        startModifiedDate,
        endModifiedDate,
      )
    }
  
    // Apply filter for role if provided
    if (roleName && roleName.length > 0) {
      optimizedFilterRoleUsers(roleName[0])
    }
  
    // Apply the filter state
    onApplyFilter(currentFilters)
  
    // Close the modal
    onClose()
  }, [currentFilters, onApplyFilter, onClose, handleAlertMessage])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose, initialFilters])

  console.log("current filters ", currentFilters)
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: '400px', p: 2 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Typography variant="h6" fontFamily="Gabarito">
          Filter By:
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ mt: 2 }}>
            {config.fields.map((fieldConfig) => (
              <UserFilterField
                key={fieldConfig.field}
                config={fieldConfig}
                filters={currentFilters}
                onFilterChange={handleUpdateFilter}
                onDateRangeChange={handleDateRangeChange}
                tab={tab}
              />
            ))}
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ p: 0, mt: 2 }}>
        <Button
          onClick={handleReset}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontFamily: 'Gabarito',
          }}
        >
          Reset Filter
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            bgcolor: 'secondary.main',
            textTransform: 'none',
            fontFamily: 'Gabarito',
            '&:hover': {
              bgcolor: 'secondary.dark',
            },
          }}
        >
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
  )
}
